/**
 * Reading several customers at once (c133), in the browser: a paste of emails, or a CSV of today's jobs from a
 * field service tool. Pure and dependency free, so the app turns the paste or the file into rows on the phone and
 * the list itself never reaches us. The API then judges each row (`reviewAsksCheck`) and stores nothing but the
 * asks that are sent.
 *
 * A CSV is for today's jobs only (c132: never a catch up of past customers), so every row needs a job date and
 * `parseJobDate` with `localDay` is how the API holds a row to today in the business's time zone.
 */

export interface ParsedAskRow {
  firstName?: string;
  email: string;
  jobDate?: string;
}

export interface ParsedAsks {
  rows: ParsedAskRow[];
  /** Lines with words in them and no email we could find. */
  unread: number;
  /** Rows past `max` that were dropped. */
  dropped: number;
}

const EMAIL_LIKE = /[^\s<>,;:"'()[\]]+@[^\s<>,;:"'()[\]]+/g;

function tidyEmail(raw: string): string {
  return raw.replace(/^mailto:/i, '').replace(/[.,;:!?]+$/, '').trim();
}

/** "Maria Gonzalez" → "Maria". Titles and anything that isn't a name are dropped. */
function firstNameOf(raw: string): string | undefined {
  const words = raw
    .replace(/["'<>()[\]]/g, ' ')
    .split(/[\s,;:|]+/)
    .filter((w) => /^\p{L}[\p{L}'.-]*$/u.test(w) && !/^(mr|mrs|ms|miss|dr|mx)\.?$/i.test(w));
  const first = words[0];
  if (!first || first.length > 60) return undefined;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

/**
 * A paste: one customer per line, or emails separated by commas, spaces or semicolons. A line with one email and
 * some words keeps the first word as the name ("Maria Gonzalez maria@example.com", "Maria <maria@example.com>").
 * Repeats are kept: the API marks the second one, so the owner sees it.
 */
export function parseAskPaste(text: string, max = 200): ParsedAsks {
  const rows: ParsedAskRow[] = [];
  let unread = 0;
  for (const line of String(text ?? '').split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const found = [...trimmed.matchAll(EMAIL_LIKE)].map((m) => tidyEmail(m[0]));
    if (found.length === 0) {
      if (/\p{L}/u.test(trimmed)) unread++;
      continue;
    }
    if (found.length === 1) {
      const name = firstNameOf(trimmed.replace(EMAIL_LIKE, ' '));
      rows.push({ ...(name ? { firstName: name } : {}), email: found[0]! });
    } else for (const email of found) rows.push({ email });
  }
  return { rows: rows.slice(0, max), unread, dropped: Math.max(0, rows.length - max) };
}

/** One CSV line into cells: commas, tabs or semicolons, with double-quoted cells and doubled quotes inside them. */
export function splitCsvLine(line: string, delimiter = ','): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cur += ch;
    } else if (ch === '"' && cur.trim() === '') quoted = true;
    else if (ch === delimiter) {
      out.push(cur.trim());
      cur = '';
    } else cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function delimiterOf(header: string): string {
  const counts = [',', '\t', ';'].map((d) => [d, splitCsvLine(header, d).length] as const).sort((a, b) => b[1] - a[1]);
  return counts[0]![0];
}

export interface ParsedCsv extends ParsedAsks {
  /** False when the file has no column we can read as the job's date: the import can't be held to today. */
  hasDateColumn: boolean;
  /** False when the file has no email column. */
  hasEmailColumn: boolean;
}

/**
 * A CSV of jobs: a header row, then one job per row. Columns are found by their names, in any order: an email
 * column, a job date column (completed, closed, finished, visit, job or plain date) and, when there is one, a
 * name column (a first name column wins over a full name).
 */
export function parseAskCsv(text: string, max = 200): ParsedCsv {
  const raw = String(text ?? '');
  // A byte order mark at the start of the file (Excel writes one) isn't part of the first column's name.
  const lines = (raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw).split(/\r?\n/).filter((l) => l.trim() !== '');
  const empty = { rows: [], unread: 0, dropped: 0 };
  if (lines.length === 0) return { ...empty, hasDateColumn: false, hasEmailColumn: false };
  const delimiter = delimiterOf(lines[0]!);
  const header = splitCsvLine(lines[0]!, delimiter).map((h) => h.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim());
  const find = (...tests: RegExp[]): number => {
    for (const re of tests) {
      const i = header.findIndex((h) => re.test(h));
      if (i >= 0) return i;
    }
    return -1;
  };
  const iEmail = find(/^e ?mail$/, /e ?mail/);
  const iDate = find(/complet|closed|finish/, /visit|job date|service date|work date/, /^date$/, /date/);
  const iFirst = find(/^first( name)?$/, /first name/, /customer|client|contact|^name$/, /name/);
  const rows: ParsedAskRow[] = [];
  let unread = 0;
  if (iEmail >= 0) {
    for (const line of lines.slice(1)) {
      const cells = splitCsvLine(line, delimiter);
      const email = tidyEmail(cells[iEmail] ?? '');
      if (!email) {
        unread++;
        continue;
      }
      const name = iFirst >= 0 ? firstNameOf(cells[iFirst] ?? '') : undefined;
      const date = iDate >= 0 ? (cells[iDate] ?? '').slice(0, 40) : '';
      rows.push({ ...(name ? { firstName: name } : {}), email, ...(date ? { jobDate: date } : {}) });
    }
  }
  return { rows: rows.slice(0, max), unread, dropped: Math.max(0, rows.length - max), hasDateColumn: iDate >= 0, hasEmailColumn: iEmail >= 0 };
}

const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

function day(y: number, m: number, d: number): string | null {
  if (!(y >= 2000 && y <= 2100 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/**
 * A job date as a field service tool writes it, to YYYY-MM-DD: 2026-10-08, 2026-10-08T14:30:00Z, 10/8/2026,
 * 10/08/26, 10-08-2026, Oct 8, 2026 or 8 October 2026. Slashes are read month first, as in the US. Null when it
 * can't be read, so the row is skipped and never guessed at. A full timestamp with a zone (2026-10-09T02:00:00Z) is
 * turned into the day it was in `timeZone`, so an evening job doesn't read as tomorrow.
 */
export function parseJobDate(raw: string, timeZone?: string): string | null {
  const s = String(raw ?? '').trim();
  if (timeZone && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/.test(s)) {
    const at = Date.parse(s);
    return Number.isNaN(at) ? null : localDay(new Date(at), timeZone);
  }
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/.exec(s);
  if (m) return day(Number(m[1]), Number(m[2]), Number(m[3]));
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4}|\d{2})(?:[,\s].*)?$/.exec(s);
  if (m) return day(m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]), Number(m[1]), Number(m[2]));
  m = /^([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})(?:[,\s].*)?$/.exec(s);
  if (m && monthOf(m[1]!)) return day(Number(m[3]), monthOf(m[1]!)!, Number(m[2]));
  m = /^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]{3,9})\.?,?\s+(\d{4})(?:[,\s].*)?$/.exec(s);
  if (m && monthOf(m[2]!)) return day(Number(m[3]), monthOf(m[2]!)!, Number(m[1]));
  return null;
}

/** A month from its name or its usual short form ("Oct", "Sept", "October"), never from a word that only starts like one. */
function monthOf(word: string): number | null {
  const w = word.toLowerCase();
  const i = MONTH_NAMES.findIndex((name) => name === w || name.slice(0, 3) === w || (name === 'september' && w === 'sept'));
  return i >= 0 ? i + 1 : null;
}

/** Today's date (YYYY-MM-DD) in a time zone. */
export function localDay(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)!.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}
